import React from "react";
import { useQuery } from "@apollo/client";
import { Center, Spinner } from "@chakra-ui/react";
import { GET_MY_SHELV_ACCESS } from "../queries";
import ProLocked from "../ProLocked";

/**
 * PRO GATE — the paywall, made real.
 *
 * The four Pro routes used to point at `ProLocked` directly, which meant the
 * explainer rendered for EVERYONE — including a paying subscriber. Holding
 * `SHELV_PRO` did nothing on the client, because nothing on the client read it.
 * That was found on 2026-08-29 when Tom, who had just been granted the
 * permission, still could not get in.
 *
 * Free users still REACH these routes and are told what Pro is, rather than
 * hitting an access-denied wall on a nav item they can see. The data itself is
 * gated server-side on `shelvPermissions.pro`, so this component is the story,
 * not the security.
 *
 * Loading is its own state: rendering the paywall while the permission query is
 * still in flight would flash "upgrade" at someone who has already paid.
 */
export const ProGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, loading } = useQuery(GET_MY_SHELV_ACCESS, { fetchPolicy: "cache-and-network" });

  if (loading && !data) {
    return (
      <Center py={20}>
        <Spinner />
      </Center>
    );
  }

  const permissions: string[] = data?.myClient?.permissions ?? [];
  const hasPro = permissions.includes("SHELV_PRO") || permissions.includes("MASTER_ADMIN");

  return hasPro ? <>{children}</> : <ProLocked />;
};

/** Wrap a page component for a route table entry. */
export const withProGate = (Page: React.ComponentType): React.FC => {
  const Wrapped: React.FC = () => (
    <ProGate>
      <Page />
    </ProGate>
  );
  Wrapped.displayName = `withProGate(${Page.displayName || Page.name || "Page"})`;
  return Wrapped;
};

export default ProGate;
